/* ============================================================
 * Vidéothèque clients — comportements de la landing dédiée
 * ------------------------------------------------------------
 * 1. Barre de filtres : affiche une seule bande gamme, ou tout.
 * 2. Carousels témoignages : flèches qui pilotent un scroll natif.
 * 3. Vignettes : la première frame du fichier n'est chargée que
 *    lorsque la carte approche du viewport, deux fichiers à la fois.
 * 4. Lecture vidéo en façade : le player n'est injecté qu'au clic
 *    (une page = ~30 vidéos, on ne charge pas 30 players au load).
 *
 * Aucun preventDefault sur touchmove : le scroll horizontal est
 * du scroll natif + scroll-snap, sinon le scroll vertical mobile
 * meurt sur les sliders.
 * ============================================================ */
(function () {
  'use strict';

  /* ----------------------------------------------------------
   * Utilitaires
   * -------------------------------------------------------- */

  // Un shopify-section wrappe chaque section : c'est lui qu'il faut
  // masquer, sinon la marge du wrapper reste visible.
  function sectionWrapper(el) {
    return el.closest('.shopify-section') || el;
  }

  // Hauteur du header sticky du thème. Elle change entre mobile et desktop, et
  // la barre de filtres doit se caler juste dessous : on la publie en custom
  // property pour que le CSS s'en serve comme valeur de `top`.
  function measureHeader() {
    var offset = 0;
    document
      .querySelectorAll('header, .header, .site-header, .shopify-section-header, [data-sticky], sticky-header')
      .forEach(function (el) {
        var pos = window.getComputedStyle(el).position;
        if (pos === 'fixed' || pos === 'sticky') {
          offset = Math.max(offset, el.offsetHeight);
        }
      });
    document.documentElement.style.setProperty('--vt-header-h', offset + 'px');
    return offset;
  }

  // Ce qui masque le haut du viewport : le header, plus la barre de filtres
  // quand elle est collante. Sans ça, filtrer scrolle la bande sous la barre.
  function scrollOffset() {
    var offset = measureHeader();
    var row = document.querySelector('[data-vt-filter-row]');
    if (row) {
      var wrapper = row.closest('.shopify-section') || row;
      if (window.getComputedStyle(wrapper).position === 'sticky') {
        offset += wrapper.offsetHeight;
      }
    }
    return offset;
  }

  function scrollToElement(el) {
    if (!el) return;
    var y = el.getBoundingClientRect().top + window.pageYOffset - scrollOffset() - 12;
    window.scrollTo({ top: y < 0 ? 0 : y, behavior: 'smooth' });
  }

  /* ----------------------------------------------------------
   * 1. Filtres
   * -------------------------------------------------------- */
  function initFilters(row) {
    if (!row || row.dataset.vtBound === '1') return;
    row.dataset.vtBound = '1';

    var pills = Array.prototype.slice.call(row.querySelectorAll('[data-vt-target]'));
    if (!pills.length) return;

    function apply(key) {
      row.dataset.vtActive = key;

      pills.forEach(function (pill) {
        pill.setAttribute('aria-pressed', pill.dataset.vtTarget === key ? 'true' : 'false');
      });

      document.querySelectorAll('[data-vt-gamme]').forEach(function (gamme) {
        var match = key === '' || gamme.dataset.vtGamme === key;
        sectionWrapper(gamme).classList.toggle('bnz-vt-hidden', !match);
      });
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var key = pill.dataset.vtTarget || '';
        // Re-cliquer la pastille active remet la page complète.
        var next = row.dataset.vtActive === key ? '' : key;
        apply(next);

        if (next === '') {
          scrollToElement(row.closest('.bnz-vt-filters') || row);
        } else {
          var target = document.querySelector('[data-vt-gamme="' + next + '"]');
          scrollToElement(target ? sectionWrapper(target) : row);
        }
      });
    });

    apply(row.dataset.vtActive || '');
  }

  /* ----------------------------------------------------------
   * 2. Carousel témoignages
   * -------------------------------------------------------- */
  // Une gamme peut afficher deux lignes de témoignages. Le header n'a qu'un
  // seul jeu de flèches, qui fait défiler les deux pistes en même temps.
  function initCarousel(root) {
    if (!root || root.dataset.vtBound === '1') return;
    root.dataset.vtBound = '1';

    var tracks = Array.prototype.slice.call(root.querySelectorAll('[data-vt-track]'));
    var prev = root.querySelector('[data-vt-prev]');
    var next = root.querySelector('[data-vt-next]');
    if (!tracks.length) return;

    function step(track) {
      var first = track.firstElementChild;
      if (!first) return track.clientWidth;
      var gap = parseFloat(window.getComputedStyle(track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    // 2px de tolérance : les largeurs fractionnaires ne tombent jamais juste.
    function atStart(track) {
      return track.scrollLeft <= 2;
    }

    function atEnd(track) {
      return track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }

    // Les deux lignes n'ont pas forcément le même nombre de cartes : on ne
    // désactive une flèche que quand toutes les pistes sont en butée.
    function refresh() {
      if (prev) prev.disabled = tracks.every(atStart);
      if (next) next.disabled = tracks.every(atEnd);
    }

    function scrollAll(direction) {
      tracks.forEach(function (track) {
        track.scrollBy({ left: direction * step(track), behavior: 'smooth' });
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        scrollAll(-1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        scrollAll(1);
      });
    }

    tracks.forEach(function (track) {
      track.addEventListener('scroll', refresh, { passive: true });
    });
    window.addEventListener('resize', refresh);
    refresh();
  }

  /* ----------------------------------------------------------
   * 3. Vignettes : première frame du fichier, chargée au compte-gouttes
   * ------------------------------------------------------------
   * Faute de miniature chargée dans le bloc, la carte affiche la première
   * frame du fichier. La section rend donc une balise <video> sans src, et
   * c'est ici qu'on décide quand la remplir.
   *
   * Trois garde-fous, parce que les témoignages sont des fichiers bruts
   * sortis d'un téléphone (des dizaines de Mo chacun, conteneur QuickTime,
   * souvent en HEVC) :
   *   - on ne charge que ce qui approche du viewport ;
   *   - deux fichiers à la fois, pas trente ;
   *   - au moindre échec on retire la balise, pour que la carte retombe sur
   *     son fond de gamme au lieu de rester un rectangle noir.
   * -------------------------------------------------------- */

  // Firefox ne lit pas le conteneur QuickTime, et Chrome/Edge ne décodent le
  // HEVC que si la machine sait le faire. Inutile de télécharger 50 Mo pour
  // s'en apercevoir : on demande au navigateur avant.
  var canPlayMov = (function () {
    try {
      return document.createElement('video').canPlayType('video/quicktime') !== '';
    } catch (e) {
      return true;
    }
  })();

  var THUMB_MAX = 2; // chargements simultanés
  var THUMB_TIMEOUT = 10000; // un moov en fin de fichier peut ne jamais arriver
  var thumbQueue = [];
  var thumbActive = 0;

  function thumbDrop(video) {
    // Couper le src avant de sortir du DOM, sinon le téléchargement en cours
    // continue jusqu'au bout pour un élément que plus personne ne regarde.
    video.removeAttribute('src');
    try {
      video.load();
    } catch (e) {}
    if (video.parentNode) video.parentNode.removeChild(video);
  }

  // Trois issues :
  //   'ok'      la frame est peinte, on garde la balise ;
  //   'refused' le navigateur ne sait pas lire ce format. On retire aussi le
  //             bouton play, qui n'ouvrirait qu'un lecteur mort, et la carte
  //             se lit comme un verbatim posé sur le fond de gamme ;
  //   'timeout' trop long à arriver. On abandonne la vignette seulement : au
  //             clic, la lecture a toutes ses chances.
  function thumbDone(video, outcome) {
    if (video.dataset.vtThumbDone === '1') return;
    video.dataset.vtThumbDone = '1';

    if (video.vtTimer) {
      clearTimeout(video.vtTimer);
      video.vtTimer = null;
    }

    if (outcome !== 'ok') {
      var media = video.closest('.bnz-vt-card__media');
      thumbDrop(video);
      if (outcome === 'refused' && media) {
        var play = media.querySelector('[data-vt-play]');
        if (play) play.parentNode.removeChild(play);
        media.classList.add('bnz-vt-card__media--unplayable');
      }
    }

    thumbActive -= 1;
    thumbNext();
  }

  function thumbStart(video) {
    var src = video.dataset.vtThumb;
    if (!src) return;

    thumbActive += 1;

    video.addEventListener(
      'loadeddata',
      function () {
        thumbDone(video, 'ok');
      },
      { once: true }
    );

    video.addEventListener(
      'error',
      function () {
        thumbDone(video, 'refused');
      },
      { once: true }
    );

    video.vtTimer = setTimeout(function () {
      thumbDone(video, 'timeout');
    }, THUMB_TIMEOUT);

    video.preload = 'metadata';
    // #t=0.1 : sans fragment, Chrome s'arrête aux métadonnées et ne peint
    // aucune frame, la carte reste noire.
    video.src = src + '#t=0.1';
  }

  function thumbNext() {
    while (thumbActive < THUMB_MAX && thumbQueue.length) {
      thumbStart(thumbQueue.shift());
    }
  }

  function thumbEnqueue(video) {
    if (video.dataset.vtThumbQueued === '1') return;
    video.dataset.vtThumbQueued = '1';

    var src = video.dataset.vtThumb || '';
    if (!canPlayMov && src.split('?')[0].slice(-4).toLowerCase() === '.mov') {
      thumbActive += 1; // thumbDone rend la place qu'on vient de prendre
      thumbDone(video, 'refused');
      return;
    }

    thumbQueue.push(video);
  }

  function initThumbs(root) {
    var videos = Array.prototype.slice.call(root.querySelectorAll('video[data-vt-thumb]')).filter(
      function (video) {
        if (video.dataset.vtThumbBound === '1') return false;
        video.dataset.vtThumbBound = '1';
        return true;
      }
    );
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(thumbEnqueue);
      thumbNext();
      return;
    }

    // Une carte hors piste ou dans une bande masquée par les filtres n'est pas
    // intersectée : elle ne se charge qu'une fois amenée à l'écran.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          thumbEnqueue(entry.target);
        });
        thumbNext();
      },
      { rootMargin: '200px' }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  /* ----------------------------------------------------------
   * 4. Lecture vidéo en façade
   * -------------------------------------------------------- */
  function initPlay(root) {
    root.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-vt-play]');
      if (!btn || !root.contains(btn)) return;

      var media = btn.closest('.bnz-vt-card__media');
      if (!media) return;

      var embed = btn.dataset.vtEmbed;
      var file = btn.dataset.vtFile;
      var label = btn.dataset.vtLabel || 'Témoignage vidéo';
      var poster = btn.dataset.vtPoster || '';
      if (!embed && !file) return;

      var player;

      if (embed) {
        player = document.createElement('iframe');
        player.src = embed;
        player.title = label;
        player.loading = 'lazy';
        player.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';
        player.allowFullscreen = true;
        player.referrerPolicy = 'strict-origin-when-cross-origin';
        player.setAttribute('frameborder', '0');
      } else {
        player = document.createElement('video');
        player.src = file;
        player.title = label;
        player.controls = true;
        player.autoplay = true;
        player.playsInline = true;
        player.preload = 'metadata';
        if (poster) player.poster = poster;

        // Un HEVC que la machine ne décode pas échoue ici, après le clic. On
        // remet la carte telle qu'elle était plutôt que de laisser un lecteur
        // noir et muet, et on retire le bouton qui ne mène nulle part.
        var before = media.innerHTML;
        player.addEventListener(
          'error',
          function () {
            media.innerHTML = before;
            media.querySelectorAll('video[data-vt-thumb]:not([src])').forEach(function (empty) {
              empty.parentNode.removeChild(empty);
            });
            var dead = media.querySelector('[data-vt-play]');
            if (dead) dead.parentNode.removeChild(dead);
            media.classList.add('bnz-vt-card__media--unplayable');
          },
          { once: true }
        );
      }

      media.innerHTML = '';
      media.appendChild(player);
    });
  }

  /* ----------------------------------------------------------
   * Bootstrap
   * -------------------------------------------------------- */
  function init(scope) {
    var root = scope || document;

    measureHeader();

    root.querySelectorAll('[data-vt-filter-row]').forEach(initFilters);
    root.querySelectorAll('[data-vt-carousel]').forEach(initCarousel);

    initThumbs(root);

    root.querySelectorAll('.bnz-vt-gamme').forEach(function (gamme) {
      if (gamme.dataset.vtPlayBound === '1') return;
      gamme.dataset.vtPlayBound = '1';
      initPlay(gamme);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Le header change de hauteur au resize, et les webfonts qui arrivent après
  // le premier rendu peuvent le faire grandir.
  window.addEventListener('resize', measureHeader);
  window.addEventListener('load', measureHeader);

  // Theme editor : les sections sont réinjectées à chaud.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
